import React, { useState, useEffect } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "حاسبة تخفيف المحاليل - حساب التخفيف باستخدام معادلة C1V1=C2V2";
const DESCRIPTION =
  "حاسبة تخفيف المحاليل المجانية - احسب التركيز والحجم المطلوب لتخفيف المحاليل الكيميائية بدقة. استخدم معادلة C1V1=C2V2 واحصل على النتائج فوراً مع معامل التخفيف وحجم المذيب.";
const KEYWORDS =
  "حاسبة تخفيف المحاليل, معادلة التخفيف, C1V1=C2V2, حساب التخفيف, معامل التخفيف, تخفيف المحاليل الكيميائية, حاسبة المولارية, dilution calculator, تخفيف المركزات";
const OG_URL = "https://do-calculate.com/calculator/ar/solution-dilution/";

const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "حاسبة تخفيف المحاليل",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "UtilityApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "الرئيسية",
        item: "https://do-calculate.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "الحاسبات",
        item: "https://do-calculate.com/ar/all-calculators/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "حاسبة تخفيف المحاليل",
        item: OG_URL,
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "ما هي معادلة تخفيف المحاليل؟",
        acceptedAnswer: {
          "@type": "Answer",
          text: "معادلة تخفيف المحاليل هي C1V1 = C2V2، حيث C1 هو التركيز الأولي، V1 حجم المحلول الأولي، C2 التركيز النهائي، وV2 الحجم النهائي. تستخدم هذه المعادلة لحساب أي قيمة مجهولة عند معرفة القيم الثلاث الأخرى.",
        },
      },
      {
        "@type": "Question",
        name: "كيف أحسب حجم المذيب المطلوب للتخفيف؟",
        acceptedAnswer: {
          "@type": "Answer",
          text: "حجم المذيب المطلوب = الحجم النهائي - حجم المحلول الأولي. على سبيل المثال، إذا كنت تريد تحضير 100 مل من محلول مخفف وتحتاج 10 مل من المحلول المركز، فإن حجم المذيب = 100 - 10 = 90 مل.",
        },
      },
      {
        "@type": "Question",
        name: "ما هو معامل التخفيف؟",
        acceptedAnswer: {
          "@type": "Answer",
          text: "معامل التخفيف هو النسبة بين التركيز الأولي والتركيز النهائي، أو النسبة بين الحجم النهائي والحجم الأولي. معامل تخفيف 10 يعني أن التركيز انخفض إلى عُشر قيمته الأصلية.",
        },
      },
    ],
  },
];

const CONCENTRATION_UNITS = {
  M: { value: 1, name: "مولار (M)" },
  mM: { value: 0.001, name: "ميلي مولار (mM)" },
  "µM": { value: 0.000001, name: "ميكرو مولار (µM)" },
  "nM": { value: 0.000000001, name: "نانو مولار (nM)" },
  "%": { value: 1, name: "نسبة مئوية (%)" },
  "g/L": { value: 1, name: "جرام/لتر (g/L)" },
  "mg/mL": { value: 1, name: "ملليجرام/ملليلتر (mg/mL)" },
  "µg/mL": { value: 1, name: "ميكروجرام/ملليلتر (µg/mL)" },
};

const VOLUME_UNITS = {
  L: { value: 1, name: "لتر (L)" },
  mL: { value: 0.001, name: "ملليلتر (mL)" },
  "µL": { value: 0.000001, name: "ميكرولتر (µL)" },
};

function DilutionCalculator() {
  const [calculationMode, setCalculationMode] = useState("calculate_V1");
  const [C1, setC1] = useState("");
  const [V1, setV1] = useState("");
  const [C2, setC2] = useState("");
  const [V2, setV2] = useState("");
  const [C1Unit, setC1Unit] = useState("M");
  const [V1Unit, setV1Unit] = useState("mL");
  const [C2Unit, setC2Unit] = useState("mM");
  const [V2Unit, setV2Unit] = useState("mL");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (shouldCalculate()) {
      calculateResult();
    }
  }, [C1, V1, C2, V2, C1Unit, V1Unit, C2Unit, V2Unit, calculationMode]);

  const shouldCalculate = () => {
    const inputs = [C1, V1, C2, V2];
    const filledInputs = inputs.filter(
      (val) => val !== "" && val !== null && !isNaN(parseFloat(val))
    );
    if (calculationMode === "dilution_factor") {
      return filledInputs.length >= 2;
    }
    return filledInputs.length >= 3;
  };

  const calculateResult = () => {
    setError("");
    try {
      const c1Val = parseFloat(C1);
      const v1Val = parseFloat(V1);
      const c2Val = parseFloat(C2);
      const v2Val = parseFloat(V2);

      if (calculationMode !== "dilution_factor") {
        const values = [c1Val, v1Val, c2Val, v2Val].filter((v) => !isNaN(v));
        if (values.some((v) => v <= 0)) {
          setError("جميع القيم يجب أن تكون أكبر من صفر");
          setResult(null);
          return;
        }
      }

      let calculatedValue, dilutionFactor, solventVolume, calculationType;

      switch (calculationMode) {
        case "calculate_V1":
          if (isNaN(c1Val) || isNaN(c2Val) || isNaN(v2Val)) {
            setError("يرجى إدخال قيم C1 و C2 و V2");
            setResult(null);
            return;
          }
          if (c2Val > c1Val) {
            setError("التركيز النهائي لا يمكن أن يكون أكبر من التركيز الأولي");
            setResult(null);
            return;
          }
          const c1InBaseUnit = c1Val * (C1Unit === "%" ? 1 : CONCENTRATION_UNITS[C1Unit].value);
          const c2InBaseUnit = c2Val * (C2Unit === "%" ? 1 : CONCENTRATION_UNITS[C2Unit].value);
          const v2InLiters = v2Val * VOLUME_UNITS[V2Unit].value;
          calculatedValue = (c2InBaseUnit * v2InLiters) / c1InBaseUnit;
          calculatedValue = calculatedValue / VOLUME_UNITS[V1Unit].value;
          dilutionFactor = c1Val / c2Val;
          solventVolume = v2Val - calculatedValue;
          calculationType = "حجم المحلول الأولي (V1)";
          setResult({
            value: calculatedValue, unit: V1Unit, type: calculationType,
            dilutionFactor, solventVolume, solventUnit: V2Unit,
            C1: c1Val, C1Unit, V1: calculatedValue, V1Unit, C2: c2Val, C2Unit, V2: v2Val, V2Unit
          });
          break;

        case "calculate_C1":
          if (isNaN(v1Val) || isNaN(c2Val) || isNaN(v2Val)) {
            setError("يرجى إدخال قيم V1 و C2 و V2");
            setResult(null);
            return;
          }
          const c2Base = c2Val * (C2Unit === "%" ? 1 : CONCENTRATION_UNITS[C2Unit].value);
          const v1InLiters = v1Val * VOLUME_UNITS[V1Unit].value;
          const v2InL = v2Val * VOLUME_UNITS[V2Unit].value;
          calculatedValue = (c2Base * v2InL) / v1InLiters;
          calculatedValue = calculatedValue / (C1Unit === "%" ? 1 : CONCENTRATION_UNITS[C1Unit].value);
          dilutionFactor = calculatedValue / c2Val;
          solventVolume = v2Val - v1Val;
          calculationType = "التركيز الأولي (C1)";
          setResult({
            value: calculatedValue, unit: C1Unit, type: calculationType,
            dilutionFactor, solventVolume, solventUnit: V2Unit,
            C1: calculatedValue, C1Unit, V1: v1Val, V1Unit, C2: c2Val, C2Unit, V2: v2Val, V2Unit
          });
          break;

        case "calculate_V2":
          if (isNaN(c1Val) || isNaN(v1Val) || isNaN(c2Val)) {
            setError("يرجى إدخال قيم C1 و V1 و C2");
            setResult(null);
            return;
          }
          if (c2Val > c1Val) {
            setError("التركيز النهائي لا يمكن أن يكون أكبر من التركيز الأولي");
            setResult(null);
            return;
          }
          const c1Base2 = c1Val * (C1Unit === "%" ? 1 : CONCENTRATION_UNITS[C1Unit].value);
          const c2Base2 = c2Val * (C2Unit === "%" ? 1 : CONCENTRATION_UNITS[C2Unit].value);
          const v1InL2 = v1Val * VOLUME_UNITS[V1Unit].value;
          calculatedValue = (c1Base2 * v1InL2) / c2Base2;
          calculatedValue = calculatedValue / VOLUME_UNITS[V2Unit].value;
          dilutionFactor = c1Val / c2Val;
          solventVolume = calculatedValue - v1Val;
          calculationType = "الحجم النهائي (V2)";
          setResult({
            value: calculatedValue, unit: V2Unit, type: calculationType,
            dilutionFactor, solventVolume, solventUnit: V2Unit,
            C1: c1Val, C1Unit, V1: v1Val, V1Unit, C2: c2Val, C2Unit, V2: calculatedValue, V2Unit
          });
          break;

        case "calculate_C2":
          if (isNaN(c1Val) || isNaN(v1Val) || isNaN(v2Val)) {
            setError("يرجى إدخال قيم C1 و V1 و V2");
            setResult(null);
            return;
          }
          if (v1Val > v2Val) {
            setError("الحجم النهائي يجب أن يكون أكبر من أو يساوي الحجم الأولي");
            setResult(null);
            return;
          }
          const c1Base3 = c1Val * (C1Unit === "%" ? 1 : CONCENTRATION_UNITS[C1Unit].value);
          const v1InL3 = v1Val * VOLUME_UNITS[V1Unit].value;
          const v2InL3 = v2Val * VOLUME_UNITS[V2Unit].value;
          calculatedValue = (c1Base3 * v1InL3) / v2InL3;
          calculatedValue = calculatedValue / (C2Unit === "%" ? 1 : CONCENTRATION_UNITS[C2Unit].value);
          dilutionFactor = c1Val / calculatedValue;
          solventVolume = v2Val - v1Val;
          calculationType = "التركيز النهائي (C2)";
          setResult({
            value: calculatedValue, unit: C2Unit, type: calculationType,
            dilutionFactor, solventVolume, solventUnit: V2Unit,
            C1: c1Val, C1Unit, V1: v1Val, V1Unit, C2: calculatedValue, C2Unit, V2: v2Val, V2Unit
          });
          break;

        case "dilution_factor":
          let df;
          if (!isNaN(c1Val) && !isNaN(c2Val)) {
            df = c1Val / c2Val;
          } else if (!isNaN(v2Val) && !isNaN(v1Val)) {
            df = v2Val / v1Val;
          } else {
            setError("يرجى إدخال إما (C1 و C2) أو (V1 و V2) لحساب معامل التخفيف");
            setResult(null);
            return;
          }
          setResult({
            dilutionFactor: df, type: "معامل التخفيف",
            C1: c1Val || null, C1Unit, C2: c2Val || null, C2Unit,
            V1: v1Val || null, V1Unit, V2: v2Val || null, V2Unit
          });
          break;

        default:
          setError("يرجى اختيار نوع الحساب");
          setResult(null);
      }
    } catch (err) {
      setError("حدث خطأ في الحساب. يرجى التحقق من القيم المدخلة.");
      setResult(null);
    }
  };

  const handleClear = () => {
    setC1("");
    setV1("");
    setC2("");
    setV2("");
    setC1Unit("M");
    setV1Unit("mL");
    setC2Unit("mM");
    setV2Unit("mL");
    setCalculationMode("calculate_V1");
    setResult(null);
    setError("");
  };

  const renderInputFields = () => {
    return (
      <>
        <div className="row">
          <div className="col-sm-12">
            <label htmlFor="calculationMode" >نوع الحساب</label>
            <select id="calculationMode" className="form-select" value={calculationMode} onChange={(e) => setCalculationMode(e.target.value)}>
              <option value="calculate_V1">حساب حجم المحلول الأولي (V1)</option>
              <option value="calculate_C1">حساب التركيز الأولي (C1)</option>
              <option value="calculate_V2">حساب الحجم النهائي (V2)</option>
              <option value="calculate_C2">حساب التركيز النهائي (C2)</option>
              <option value="dilution_factor">حساب معامل التخفيف فقط</option>
            </select>
          </div>
        </div>
        {calculationMode !== "calculate_C1" && (
          <div className="row">
            <div className="col-sm-12 col-md-6">
              <label htmlFor="C1" >التركيز الأولي (C1){calculationMode === "dilution_factor" && " - اختياري"}</label>
              <input type="number" id="C1" className="form-control" value={C1} onChange={(e) => setC1(e.target.value)} min="0" step="any" placeholder="أدخل التركيز الأولي" />
            </div>
            <div className="col-sm-12 col-md-6">
              <label htmlFor="C1Unit" >وحدة التركيز الأولي</label>
              <select id="C1Unit" className="form-select" value={C1Unit} onChange={(e) => setC1Unit(e.target.value)}>
                {Object.entries(CONCENTRATION_UNITS).map(([key, val]) => (
                  <option key={key} value={key}>{key}</option>
                ))}
              </select>
            </div>
          </div>
        )}
        {calculationMode !== "calculate_V1" && (
          <div className="row">
            <div className="col-sm-12 col-md-6">
              <label htmlFor="V1" >حجم المحلول الأولي (V1){calculationMode === "dilution_factor" && " - اختياري"}</label>
              <input type="number" id="V1" className="form-control" value={V1} onChange={(e) => setV1(e.target.value)} min="0" step="any" placeholder="أدخل الحجم الأولي" />
            </div>
            <div className="col-sm-12 col-md-6">
              <label htmlFor="V1Unit" >وحدة الحجم الأولي</label>
              <select id="V1Unit" className="form-select" value={V1Unit} onChange={(e) => setV1Unit(e.target.value)}>
                {Object.entries(VOLUME_UNITS).map(([key, val]) => (
                  <option key={key} value={key}>{key}</option>
                ))}
              </select>
            </div>
          </div>
        )}
        {calculationMode !== "calculate_C2" && (
          <div className="row">
            <div className="col-sm-12 col-md-6">
              <label htmlFor="C2" >التركيز النهائي (C2){calculationMode === "dilution_factor" && " - اختياري"}</label>
              <input type="number" id="C2" className="form-control" value={C2} onChange={(e) => setC2(e.target.value)} min="0" step="any" placeholder="أدخل التركيز النهائي" />
            </div>
            <div className="col-sm-12 col-md-6">
              <label htmlFor="C2Unit" >وحدة التركيز النهائي</label>
              <select id="C2Unit" className="form-select" value={C2Unit} onChange={(e) => setC2Unit(e.target.value)}>
                {Object.entries(CONCENTRATION_UNITS).map(([key, val]) => (
                  <option key={key} value={key}>{key}</option>
                ))}
              </select>
            </div>
          </div>
        )}
        {calculationMode !== "calculate_V2" && (
          <div className="row">
            <div className="col-sm-12 col-md-6">
              <label htmlFor="V2" >الحجم النهائي (V2){calculationMode === "dilution_factor" && " - اختياري"}</label>
              <input type="number" id="V2" className="form-control" value={V2} onChange={(e) => setV2(e.target.value)} min="0" step="any" placeholder="أدخل الحجم النهائي" />
            </div>
            <div className="col-sm-12 col-md-6">
              <label htmlFor="V2Unit" >وحدة الحجم النهائي</label>
              <select id="V2Unit" className="form-select" value={V2Unit} onChange={(e) => setV2Unit(e.target.value)}>
                {Object.entries(VOLUME_UNITS).map(([key, val]) => (
                  <option key={key} value={key}>{key}</option>
                ))}
              </select>
            </div>
          </div>
        )}
        <div className="row">
          <div className="col-sm-12">
            <button className="btn btn-secondary w-100" onClick={handleClear}>مسح الكل</button>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="rtl" style={{ direction: "rtl" }}>
      <SharedHelmet TITLE={TITLE} DESCRIPTION={DESCRIPTION} KEYWORDS={KEYWORDS} OG_URL={OG_URL} SCHEMA={SCHEMA} />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div className="bg-success text-white text-center" style={{ padding: "15px", borderRadius: "5px" }}>
            <h1 style={{ fontSize: "22px", margin: 0 }}>حاسبة تخفيف المحاليل</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                <strong>حاسبة تخفيف المحاليل</strong> أداة علمية دقيقة تساعدك على <strong>حساب التركيز والحجم</strong> المطلوب لتخفيف المحاليل الكيميائية باستخدام معادلة C₁V₁ = C₂V₂. احصل على النتائج فوراً مع معامل التخفيف وحجم المذيب المطلوب. مثالية للمختبرات الطبية، الأبحاث العلمية، والصناعات الكيميائية.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />
            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>احسب تخفيف المحلول بدقة</h2>
              </div>
              {renderInputFields()}
              {error && <div className="alert alert-danger mt-3" role="alert">{error}</div>}
            </div>

            {result && (
              <div className="result-section">
                <h3>النتائج</h3>
                <div className="table-responsive">
                  <table className="table table-bordered bmi-table">
                    <tbody>
                      {calculationMode === "dilution_factor" ? (
                        <tr>
                          <td><strong>معامل التخفيف</strong></td>
                          <td>{result.dilutionFactor.toFixed(2)}x</td>
                        </tr>
                      ) : (
                        <>
                          <tr>
                            <td><strong>{result.type}</strong></td>
                            <td>{result.value.toFixed(4)} {result.unit}</td>
                          </tr>
                          <tr>
                            <td><strong>معامل التخفيف</strong></td>
                            <td>{result.dilutionFactor.toFixed(2)}x</td>
                          </tr>
                          <tr>
                            <td><strong>حجم المذيب المطلوب</strong></td>
                            <td>{result.solventVolume > 0 ? `${result.solventVolume.toFixed(4)} ${result.solventUnit}` : "غير مطلوب"}</td>
                          </tr>
                        </>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="info-sections-container">
                  <div className="info-section">
                    <div className="info-section-header"><strong>تفاصيل الحساب</strong></div>
                    <div className="info-section-body">
                      <p style={{ lineHeight: "1.8" }}><strong>المعادلة المستخدمة:</strong> C₁V₁ = C₂V₂</p>
                      {result.C1 !== null && <p style={{ lineHeight: "1.8" }}>التركيز الأولي (C₁) = {result.C1} {result.C1Unit}</p>}
                      {result.V1 !== null && <p style={{ lineHeight: "1.8" }}>الحجم الأولي (V₁) = {result.V1.toFixed(4)} {result.V1Unit}</p>}
                      {result.C2 !== null && <p style={{ lineHeight: "1.8" }}>التركيز النهائي (C₂) = {result.C2.toFixed(4)} {result.C2Unit}</p>}
                      {result.V2 !== null && <p style={{ lineHeight: "1.8" }}>الحجم النهائي (V₂) = {result.V2.toFixed(4)} {result.V2Unit}</p>}
                      {calculationMode !== "dilution_factor" && (
                        <div className="alert alert-info" style={{ marginTop: "15px", lineHeight: "1.8" }}>
                          <strong>طريقة التحضير:</strong><br />
                          خذ {result.V1.toFixed(4)} {result.V1Unit} من المحلول المركز، ثم أضف {result.solventVolume > 0 ? `${result.solventVolume.toFixed(4)} ${result.solventUnit}` : "لا شيء"} من المذيب للحصول على {result.V2.toFixed(4)} {result.V2Unit} من المحلول المخفف.
                        </div>
                      )}
                    </div>
                  </div>
                  {result.dilutionFactor && (
                    <div className="info-section">
                      <div className="info-section-header"><strong>فهم معامل التخفيف</strong></div>
                      <div className="info-section-body">
                        <p style={{ lineHeight: "1.8" }}>
                          معامل التخفيف = {result.dilutionFactor.toFixed(2)}x يعني أن التركيز النهائي أصبح {((1 / result.dilutionFactor) * 100).toFixed(2)}% من التركيز الأولي.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            <hr />
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>

      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header"><strong>ما هي حاسبة تخفيف المحاليل؟</strong></div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>حاسبة تخفيف المحاليل أداة علمية دقيقة تساعدك على حساب الكميات المطلوبة لتخفيف المحاليل الكيميائية بسرعة وسهولة. تعتمد الحاسبة على المعادلة الكيميائية الشهيرة C₁V₁ = C₂V₂، وهي قانون أساسي في الكيمياء يعبر عن مبدأ حفظ الكتلة. ببساطة، عندما تخفف محلولاً، كمية المادة المذابة تبقى ثابتة بينما يتغير الحجم والتركيز.</p>
                <p style={{ lineHeight: "1.8" }}>استخدام الحاسبة بسيط للغاية. أولاً، حدد نوع الحساب الذي تريده: هل تحتاج لمعرفة حجم المحلول المركز؟ أم التركيز النهائي؟ أم الحجم الكلي المطلوب؟ ثانياً، أدخل القيم الثلاث المعروفة لديك. الحاسبة ستحسب تلقائياً القيمة الرابعة المجهولة. ثالثاً، احصل على النتيجة مع تفاصيل كاملة تشمل معامل التخفيف وحجم المذيب المطلوب إضافته. وأخيراً، اتبع التعليمات المفصلة لتحضير المحلول بدقة.</p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header"><strong>استخدامات حاسبة تخفيف المحاليل</strong></div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>تستخدم حاسبة التخفيف في مجالات متعددة تتطلب دقة عالية في تحضير المحاليل:</p>
                <p style={{ lineHeight: "1.8" }}><strong>المختبرات الطبية والصيدلانية:</strong> يحتاج الصيادلة والفنيون الطبيون لتخفيف الأدوية والمحاليل الوريدية بدقة متناهية. على سبيل المثال، تخفيف محلول مضاد حيوي من تركيز 100 ملجم/مل إلى 50 ملجم/مل لمريض معين. أي خطأ في الحساب قد يعرض حياة المريض للخطر، لذلك استخدام حاسبة موثوقة ضرورة وليس رفاهية.</p>
                <p style={{ lineHeight: "1.8" }}><strong>مختبرات الأبحاث والجامعات:</strong> الباحثون يحتاجون لتحضير محاليل بتراكيز محددة جداً للتجارب العلمية. سواء كانت محاليل بيولوجية أو كيميائية عضوية، الدقة في التخفيف تؤثر مباشرة على نتائج التجارب وقابليتها للتكرار. محلول خاطئ يعني وقت ومال وجهد ضائع.</p>
                <p style={{ lineHeight: "1.8" }}><strong>الصناعات الغذائية والمشروبات:</strong> تحتاج مصانع الأغذية لتخفيف المركزات والمنكهات والملونات بنسب دقيقة للحصول على منتج متسق الجودة. مثلاً، تخفيف مركز عصير من نسبة 60% سكر إلى 12% للمنتج النهائي. الحسابات الخاطئة تؤدي لمنتج غير مقبول تجارياً.</p>
                <p style={{ lineHeight: "1.8" }}><strong>التطبيقات البيئية والزراعية:</strong> المهندسون الزراعيون يستخدمون التخفيف لتحضير المبيدات والأسمدة السائلة بالتراكيز الآمنة والفعالة. كذلك في معالجة المياه ومحطات التنقية، حيث يتم تخفيف المواد الكيميائية المعالجة مثل الكلور لتركيز مناسب لا يضر بالصحة العامة.</p>
              </div>
            </div>


            <div className="info-section">
              <div className="info-section-header"><strong>فهم معادلة التخفيف C₁V₁ = C₂V₂</strong></div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>معادلة التخفيف الأساسية C₁V₁ = C₂V₂ تعتبر من أهم المعادلات في الكيمياء التطبيقية. دعنا نفهم كل رمز فيها:</p>
                <div className="table-responsive" style={{ marginTop: "15px" }}>
                  <table className="table table-bordered">
                    <thead>
                      <tr><th>الرمز</th><th>المعنى</th><th>الوحدات الشائعة</th></tr>
                    </thead>
                    <tbody>
                      <tr><td>C₁</td><td>التركيز الأولي للمحلول المركز</td><td>M, mM, µM, %, g/L</td></tr>
                      <tr><td>V₁</td><td>حجم المحلول المركز المطلوب</td><td>L, mL, µL</td></tr>
                      <tr><td>C₂</td><td>التركيز النهائي للمحلول المخفف</td><td>M, mM, µM, %, g/L</td></tr>
                      <tr><td>V₂</td><td>الحجم الكلي النهائي للمحلول</td><td>L, mL, µL</td></tr>
                    </tbody>
                  </table>
                </div>
                <p style={{ lineHeight: "1.8" }}>المعادلة تقول إن حاصل ضرب التركيز والحجم قبل التخفيف يساوي حاصل ضربهما بعد التخفيف. هذا منطقي لأن كمية المادة الفعلية (عدد الجزيئات) لا تتغير، فقط يتم توزيعها على حجم أكبر. تخيل أن لديك 10 مكعبات سكر في كوب ماء، إذا أضفت كوباً آخر من الماء، عدد المكعبات لم يتغير لكن الطعم أصبح أقل حلاوة.</p>
                <p style={{ lineHeight: "1.8" }}>معامل التخفيف هو النسبة بين التركيزين (C₁/C₂) أو بين الحجمين (V₂/V₁). معامل تخفيف 10x يعني أنك جعلت المحلول أضعف بعشر مرات. في المختبرات، تستخدم التخفيفات المتسلسلة (Serial Dilutions) حيث يتم التخفيف على مراحل، كل مرحلة بمعامل ثابت مثل 1:10، للحصول على تراكيز صغيرة جداً بدقة عالية.</p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header"><strong>أمثلة عملية على تخفيف المحاليل</strong></div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>دعنا نستعرض أمثلة واقعية توضح كيفية استخدام حاسبة التخفيف:</p>
                <p style={{ lineHeight: "1.8" }}><strong>مثال 1 - مختبر طبي:</strong> لديك محلول ملح (NaCl) بتركيز 5 مولار، وتحتاج 250 مل من محلول 0.9%. كم مليلتر تحتاج من المحلول المركز؟ الحل: استخدم الحاسبة بإدخال C₁ = 5M، C₂ = 0.9%، V₂ = 250mL. ستحصل على V₁ ≈ 3.9mL. أي خذ 3.9 مل من المحلول المركز وأضف 246.1 مل ماء مقطر.</p>
                <p style={{ lineHeight: "1.8" }}><strong>مثال 2 - مختبر أبحاث:</strong> تريد تحضير 500 مل من محلول جلوكوز بتركيز 100 mM من محلول مخزون تركيزه 1 M. استخدم الحاسبة: C₁ = 1M، C₂ = 100mM، V₂ = 500mL. النتيجة: V₁ = 50mL. طريقة التحضير: اسكب 50 مل من محلول الجلوكوز المركز في دورق حجمي سعة 500 مل، ثم أكمل بالماء المقطر حتى العلامة.</p>
                <p style={{ lineHeight: "1.8" }}><strong>مثال 3 - صناعة غذائية:</strong> مصنع عصائر لديه مركز برتقال بتركيز 65 بريكس، ويريد إنتاج 1000 لتر من عصير جاهز بتركيز 12 بريكس. الحاسبة تعطي: خذ حوالي 185 لتر من المركز وأضف 815 لتر ماء معالج. معامل التخفيف هنا 5.4x تقريباً.</p>
                <p style={{ lineHeight: "1.8" }}><strong>مثال 4 - زراعة:</strong> مبيد حشري تركيزه 40% يجب تخفيفه إلى 2% للرش على النباتات. المزارع يحتاج 20 لتر من المحلول المخفف. الحل: V₁ = (2 × 20) / 40 = 1 لتر. أي خذ 1 لتر من المبيد المركز واخلطه مع 19 لتر ماء.</p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header"><strong>نصائح للاستخدام الصحيح والأخطاء الشائعة</strong></div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>للحصول على أفضل نتائج وتجنب الأخطاء، اتبع هذه الإرشادات الاحترافية:</p>
                <p style={{ lineHeight: "1.8" }}><strong>تأكد من وحدات القياس:</strong> أكثر خطأ شائع هو الخلط بين الوحدات. تأكد دائماً أن وحدات التركيز متوافقة (مثلاً كلها M أو كلها %)، ووحدات الحجم متوافقة أيضاً. الحاسبة تتعامل مع وحدات مختلفة، لكن من الأفضل أن تكون واعياً لما تدخله. خطأ بسيط في تحويل mL إلى L قد يعني الفرق بين محلول صحيح ومحلول مركز 1000 مرة!</p>
                <p style={{ lineHeight: "1.8" }}><strong>أضف المذاب للمذيب وليس العكس:</strong> القاعدة الذهبية في المختبرات: ضع الماء أولاً في الوعاء، ثم أضف المحلول المركز ببطء مع التحريك. هذا خاصة مهم مع الأحماض القوية والقواعد. إضافة ماء إلى حمض مركز قد تسبب تفاعلاً حرارياً عنيفاً وانفجاراً.</p>
                <p style={{ lineHeight: "1.8" }}><strong>استخدم أدوات القياس المناسبة:</strong> دقة القياس مهمة جداً. للحجوم الصغيرة (أقل من 1 مل)، استخدم ماصات دقيقة (Micropipettes). للحجوم المتوسطة، استخدم ماصات زجاجية أو اسطوانات مدرجة. للحجوم الكبيرة، استخدم دوارق حجمية.</p>
                <p style={{ lineHeight: "1.8" }}><strong>راعِ درجة الحرارة والتخزين:</strong> بعض المحاليل تتأثر بدرجة الحرارة، حيث قد يتغير تركيزها أو تتحلل مكوناتها. اقرأ دائماً تعليمات تخزين المواد الكيميائية. المحاليل المخففة عموماً أقل استقراراً من المحاليل المركزة، لذا حضّر فقط الكمية التي تحتاجها.</p>
                <p style={{ lineHeight: "1.8" }}><strong>وثّق عملك:</strong> احتفظ دائماً بسجل للمحاليل التي تحضرها: التاريخ، التركيز، الحجم، اسم من حضّره، الغرض من الاستخدام، ورقم دفعة المادة الأولية. هذا ليس فقط للسلامة، لكن أيضاً لتتبع الأخطاء إن حدثت وللامتثال لمعايير الجودة.</p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header"><strong>الأسئلة الشائعة</strong></div>
              <div className="info-section-body">
                <div style={{ marginTop: "15px" }}>
                  <h3 style={{ fontSize: "16px" }}>ما هي معادلة تخفيف المحاليل؟</h3>
                  <p style={{ lineHeight: "1.8" }}>معادلة تخفيف المحاليل هي C₁V₁ = C₂V₂، حيث C₁ هو التركيز الأولي للمحلول المركز، V₁ حجم المحلول الأولي المطلوب، C₂ التركيز النهائي المطلوب، وV₂ الحجم الكلي النهائي. تستخدم هذه المعادلة لحساب أي قيمة مجهولة عند معرفة القيم الثلاث الأخرى. المعادلة تعتمد على مبدأ حفظ الكتلة، أي أن كمية المادة المذابة تبقى ثابتة قبل وبعد التخفيف.</p>
                </div>
                <div style={{ marginTop: "15px" }}>
                  <h3 style={{ fontSize: "16px" }}>كيف أحسب حجم المذيب المطلوب للتخفيف؟</h3>
                  <p style={{ lineHeight: "1.8" }}>حجم المذيب المطلوب = الحجم النهائي (V₂) - حجم المحلول الأولي (V₁). على سبيل المثال، إذا كنت تريد تحضير 100 مل من محلول مخفف وتحتاج 10 مل من المحلول المركز، فإن حجم المذيب الذي يجب إضافته = 100 - 10 = 90 مل. في الممارسة العملية، يفضل وضع المذيب أولاً في الوعاء ثم إضافة المحلول المركز تدريجياً مع التحريك، خاصة عند التعامل مع مواد متفاعلة أو حارقة.</p>
                </div>
                <div style={{ marginTop: "15px" }}>
                  <h3 style={{ fontSize: "16px" }}>ما هو معامل التخفيف وكيف أحسبه؟</h3>
                  <p style={{ lineHeight: "1.8" }}>معامل التخفيف (Dilution Factor) هو النسبة التي تعبر عن مدى تخفيف المحلول، ويُحسب بقسمة التركيز الأولي على التركيز النهائي (C₁/C₂) أو بقسمة الحجم النهائي على الحجم الأولي (V₂/V₁). معامل تخفيف 10 (يُكتب 10x أو 1:10) يعني أن التركيز انخفض إلى عُشر قيمته الأصلية، أي أن المحلول أصبح أضعف بعشر مرات. في التخفيفات المتسلسلة، يتم ضرب معاملات التخفيف المتتالية للحصول على معامل التخفيف الإجمالي.</p>
                </div>
                <div style={{ marginTop: "15px" }}>
                  <h3 style={{ fontSize: "16px" }}>هل يمكن استخدام الحاسبة مع وحدات مختلفة؟</h3>
                  <p style={{ lineHeight: "1.8" }}>نعم، الحاسبة تدعم وحدات متعددة للتركيز (M, mM, µM, nM, %, g/L, mg/mL, µg/mL) ووحدات الحجم (L, mL, µL). الأهم أن تكون متسقاً في اختيار الوحدات المناسبة لنوع محلولك. على سبيل المثال، المحاليل البيولوجية غالباً تُقاس بالمولارية (M) أو mM، بينما المحاليل الغذائية قد تُقاس بالنسبة المئوية. الحاسبة تقوم تلقائياً بالتحويل بين الوحدات عند الحاجة للحصول على نتائج دقيقة.</p>
                </div>
                <div style={{ marginTop: "15px" }}>
                  <h3 style={{ fontSize: "16px" }}>ماذا يحدث إذا كان التركيز النهائي أكبر من الأولي؟</h3>
                  <p style={{ lineHeight: "1.8" }}>في عملية التخفيف العادية، التركيز النهائي يجب أن يكون دائماً أقل من أو يساوي التركيز الأولي. إذا أدخلت قيمة للتركيز النهائي أكبر من الأولي، ستظهر لك الحاسبة رسالة خطأ. هذا لأن التخفيف يعني إضافة مذيب لتقليل التركيز، وليس العكس. إذا أردت زيادة التركيز، فأنت تحتاج لإضافة المادة المذابة نفسها أو تبخير المذيب، وهذه عمليات مختلفة لا تُغطى بمعادلة التخفيف البسيطة.</p>
                </div>
                <div style={{ marginTop: "15px" }}>
                  <h3 style={{ fontSize: "16px" }}>كيف أتأكد من دقة النتائج؟</h3>
                  <p style={{ lineHeight: "1.8" }}>للتأكد من دقة النتائج، اتبع هذه الخطوات: أولاً، تحقق من صحة القيم المدخلة وتأكد من عدم وجود أخطاء مطبعية. ثانياً، راجع الوحدات المستخدمة وتأكد من توافقها مع طبيعة المحلول. ثالثاً، استخدم أدوات قياس معايرة ودقيقة في المختبر. رابعاً، كرر العملية الحسابية أو استخدم طريقة بديلة للتحقق. خامساً، بعد تحضير المحلول، يمكنك قياس التركيز الفعلي بطرق تحليلية مثل المعايرة أو الطيف الضوئي للتأكد من مطابقته للتركيز المستهدف.</p>
                </div>
                <div style={{ marginTop: "15px" }}>
                  <h3 style={{ fontSize: "16px" }}>ما الفرق بين التخفيف البسيط والتخفيف المتسلسل؟</h3>
                  <p style={{ lineHeight: "1.8" }}>التخفيف البسيط هو خطوة واحدة تخفف فيها المحلول مباشرة من التركيز الأولي إلى النهائي. التخفيف المتسلسل (Serial Dilution) هو سلسلة من التخفيفات المتتالية، حيث يُستخدم المحلول المخفف من كل خطوة كمصدر للخطوة التالية. يُستخدم التخفيف المتسلسل عند الحاجة لتراكيز منخفضة جداً يصعب تحضيرها مباشرة بدقة، مثل 10⁻⁶ M. في المختبرات الميكروبيولوجية، التخفيفات المتسلسلة ضرورية لعد البكتيريا وتحديد تركيزها في العينات.</p>
                </div>
                <div style={{ marginTop: "15px" }}>
                  <h3 style={{ fontSize: "16px" }}>هل تتغير خصائص المحلول بعد التخفيف؟</h3>
                  <p style={{ lineHeight: "1.8" }}>نعم ولا. التركيز يتغير بالتأكيد، وهذا هو الهدف من التخفيف. لكن طبيعة المادة الكيميائية نفسها لا تتغير. ما يتغير هو عدد الجزيئات في وحدة الحجم. بعض الخصائص الفيزيائية قد تتأثر مثل الكثافة واللزوجة ونقطة الغليان، لكن التغير عادة طفيف في التخفيفات العادية. في بعض الحالات، خصائص كيميائية مثل pH قد تتأثر، خاصة في المحاليل المنظمة (Buffer Solutions). كذلك، بعض المواد قد تترسب أو تفقد فعاليتها عند تخفيفها أكثر من اللازم.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DilutionCalculator;